import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { TextField, InputAdornment } from '@/shared/ui';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { toast } from '@/shared/feedback/toast';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_PASSWORD_POLICY,
  getPasswordPolicyViolation,
  passwordMeetsPolicy,
  passwordPolicyMessage,
  PASSWORD_MAX_LENGTH,
  type PasswordPolicyRules,
} from '@/shared/utils/validation';
import { useAuth } from '@/shared/context/AuthContext';
import { useOrgHomePath } from '@/shared/layout/useOrgHomePath';
import { profileAPI } from '@/modules/identity/api/profile.api';
import PasswordPolicyHints, {
  PasswordMatchHint,
} from '@/modules/identity/components/PasswordPolicyHints';

/** Auth form — slightly taller than global form.controlHeight so placeholder sits on the input axis. */
const changePasswordFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 44,
  },
  '& .MuiOutlinedInput-input': {
    py: '10px',
  },
} as const;

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const { t } = useTranslation('identity');
  const navigate = useNavigate();
  const { user, loadUser } = useAuth();
  const home = useOrgHomePath();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<ChangePasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const policy: PasswordPolicyRules = {
    ...DEFAULT_PASSWORD_POLICY,
    ...(user?.passwordPolicy || {}),
  };

  const handleInputChange = (field: keyof ChangePasswordFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePassword = (password: string): string | null => {
    const violation = getPasswordPolicyViolation(password, policy);
    if (!violation) return null;
    if (violation === 'min') {
      return t('validation.passwordMinN', {
        count: policy.passwordMinLength,
        defaultValue: `Password must be at least ${policy.passwordMinLength} characters`,
      });
    }
    if (violation === 'max') {
      return t('validation.passwordMaxN', { count: PASSWORD_MAX_LENGTH });
    }
    if (violation === 'uppercase') return t('validation.passwordUppercase');
    if (violation === 'lowercase') return t('validation.passwordLowercase');
    if (violation === 'number') return t('validation.passwordNumber');
    if (violation === 'special') return t('validation.passwordSpecial');
    return passwordPolicyMessage(policy);
  };

  const changePasswordMutation = useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string }) =>
      profileAPI.changePassword(payload),
    onSuccess: async () => {
      toast.success(t('changePassword.success'));
      await loadUser();
      navigate(home.path, { replace: true });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      console.error('Password change error:', error);
      toast.error(error.response?.data?.message || t('changePassword.failed'));
    },
  });
  const loading = changePasswordMutation.isPending;
  const canSubmit =
    formData.currentPassword.length > 0 &&
    passwordMeetsPolicy(formData.newPassword, policy) &&
    formData.confirmPassword.length > 0 &&
    formData.confirmPassword === formData.newPassword;



  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error(t('changePassword.fillAllFields'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('changePassword.passwordsDoNotMatch'));
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    });
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Box
        sx={{
          m: 'auto',
          py: 4,
          px: 2,
          width: '100%',
          maxWidth: 532,
          flexShrink: 0,
        }}
      >
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={4}>
            <Lock sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              {t('changePassword.title')}
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">{t('changePassword.securityNoticeBody')}</Typography>
          </Alert>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('changePassword.currentPassword')}
              placeholder={t('changePassword.currentPasswordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={formData.currentPassword}
              onChange={(e) => handleInputChange('currentPassword', e.target.value)}
              margin="normal"
              required
              sx={changePasswordFieldSx}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('changePassword.togglePassword')}
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <TextField
              fullWidth
              label={t('changePassword.newPassword')}
              placeholder={t('changePassword.newPasswordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              margin="normal"
              required
              helperText={<PasswordPolicyHints password={formData.newPassword} policy={policy} />}
              sx={changePasswordFieldSx}
              slotProps={{
                htmlInput: { maxLength: PASSWORD_MAX_LENGTH },
                formHelperText: { component: 'div', sx: { mx: 0, mt: 1 } },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('changePassword.togglePassword')}
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <TextField
              fullWidth
              label={t('changePassword.confirmPassword')}
              placeholder={t('changePassword.confirmPasswordPlaceholder')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              margin="normal"
              required
              helperText={
                <PasswordMatchHint
                  password={formData.newPassword}
                  confirmPassword={formData.confirmPassword}
                />
              }
              sx={changePasswordFieldSx}
              slotProps={{
                htmlInput: { maxLength: PASSWORD_MAX_LENGTH },
                formHelperText: { component: 'div', sx: { mx: 0, mt: 1 } },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('changePassword.toggleConfirmPassword')}
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !canSubmit}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ mt: 3 }}
            >
              {loading ? t('changePassword.submitting') : t('changePassword.submit')}
            </Button>
          </form>

          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              {t('changePassword.footer')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};

export default ChangePassword;

